import { useCallback, useRef } from "react";
import { useDispatch } from "react-redux";

/**
 * Keeps at most ONE in-flight request per thunk for the calling component.
 *
 * Dispatching the same thunk again while an earlier call is still running
 * aborts that earlier call, so a rapid filter / page / tab / module change
 * can't leave duplicate requests racing each other — previously the slower
 * response could land last and overwrite the list with rows for filters the
 * user had already moved off.
 *
 * The abort is awaited before the replacement goes out, so the actions land in
 * the order rejected(old) -> pending(new); that way the aborted rejection can
 * never clear the loading flags the new request just set. The slices ignore an
 * aborted rejection entirely (see the `meta.aborted` matcher), so no error
 * toast fires for a request we cancelled on purpose.
 *
 * Usage: `const run = useLatestRequest()` then `run(getAllBookings, { ... })`
 * instead of `dispatch(getAllBookings({ ... }))`. Only for createAsyncThunk
 * actions — the dispatched promise is what carries `.abort()`.
 */
const useLatestRequest = () => {
  const dispatch = useDispatch();
  const inFlightRef = useRef(new Map());

  return useCallback(
    async (thunk, arg) => {
      const key = thunk.typePrefix;
      const previous = inFlightRef.current.get(key);
      if (previous) {
        previous.abort();
        // A dispatched thunk resolves with its rejected action rather than
        // throwing, but swallow anyway — the caller only cares about the new one.
        await previous.catch(() => {});
      }

      const promise = dispatch(thunk(arg));
      inFlightRef.current.set(key, promise);
      try {
        return await promise;
      } catch (e) {
        return undefined;
      } finally {
        // Only clear if a newer call hasn't already taken the slot.
        if (inFlightRef.current.get(key) === promise) {
          inFlightRef.current.delete(key);
        }
      }
    },
    [dispatch]
  );
};

export default useLatestRequest;
